import { useEffect, useMemo, useRef } from "react";

/**
 * This provides a way to create and manage an AbortController/Signal for cancelling ongoing mutations.
 * Even if not manually calling abort, the request will be cancelled when the component unmounts.
 */
export function useMutationAbortController(): { getSignal: () => AbortSignal; abort: () => void } {
  const abortControllerRef = useRef<AbortController>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  return useMemo(
    () => ({
      getSignal() {
        if (!abortControllerRef.current) {
          abortControllerRef.current = new AbortController();
        }

        return abortControllerRef.current.signal;
      },
      abort() {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
      },
    }),
    [],
  );
}
